/**
 * Country Utilities
 *
 * Utility functions for subdomain-based country detection and resolution
 */

import { COUNTRY_CONFIG, SUPPORTED_COUNTRIES } from '../config/countryConfig';

/**
 * Extracts the country slug from the subdomain
 * @param {string} hostname - The hostname (e.g., 'peru.lotespro.com', 'localhost')
 * @returns {string|null} - The country slug (e.g., 'peru') or null if none
 */
export function getCountrySlugFromSubdomain(hostname = null) {
    if (!hostname && typeof window !== 'undefined') {
        hostname = window.location.hostname;
    }

    if (!hostname) return null;

    // Development: check for country parameter in URL
    if (isDevelopment()) {
        if (typeof window !== 'undefined') {
            const params = new URLSearchParams(window.location.search);
            const countryParam = params.get('country');
            if (countryParam) {
                // Find country by code
                const countryKey = Object.keys(COUNTRY_CONFIG).find(
                    key => COUNTRY_CONFIG[key].code === countryParam.toUpperCase()
                );
                return countryKey || null;
            }
        }
        return null;
    }

    // Production: extract from subdomain
    const parts = hostname.split('.');

    // For subdomains like peru.lotespro.com
    if (parts.length >= 3) {
        const potentialCountry = parts[0].toLowerCase();

        // Ignore 'www' subdomain
        if (potentialCountry === 'www') {
            return null;
        }

        // Check if it's a valid country
        if (SUPPORTED_COUNTRIES.includes(potentialCountry)) {
            return potentialCountry;
        }
    }

    return null;
}

/**
 * Gets the country code from the current subdomain
 * @returns {string|null} - The country code (e.g., 'PE') or null if no country detected
 */
export function getCountryFromSubdomain() {
    // Get country slug from subdomain
    const countrySlug = getCountrySlugFromSubdomain();

    if (countrySlug) {
        // Check if country slug is valid
        if (SUPPORTED_COUNTRIES.includes(countrySlug)) {
            return COUNTRY_CONFIG[countrySlug].code;
        }
        // Invalid country slug
        return 'INVALID';
    }

    // No country detected
    return null;
}

/**
 * Legacy function - now redirects to subdomain-based detection
 * @deprecated Use getCountryFromSubdomain() instead
 */
export function getCountryFromPath() {
    return getCountryFromSubdomain();
}

/**
 * Legacy function - kept for backwards compatibility
 * @deprecated Use getCountrySlugFromSubdomain() instead
 */
export function getCountrySlugFromPath() {
    return getCountrySlugFromSubdomain();
}

/**
 * Checks if the current domain is a global domain (no country subdomain)
 * @returns {boolean} - True if no country subdomain
 */
export function isGlobalPath() {
    const countrySlug = getCountrySlugFromSubdomain();
    return countrySlug === null;
}

/**
 * Gets the country configuration for a country code
 * @param {string} countryCode - The country code (e.g., 'PE')
 * @returns {object|null} - The country configuration or null if not found
 */
export function getCountryConfig(countryCode) {
    if (!countryCode) return null;

    const countryKey = Object.keys(COUNTRY_CONFIG).find(
        key => COUNTRY_CONFIG[key].code === countryCode
    );

    return countryKey ? COUNTRY_CONFIG[countryKey] : null;
}

/**
 * Gets the country key (slug) from a country code
 * @param {string} countryCode - The country code (e.g., 'PE')
 * @returns {string|null} - The country key (e.g., 'peru') or null
 */
export function getCountryKey(countryCode) {
    if (!countryCode) return null;

    return Object.keys(COUNTRY_CONFIG).find(
        key => COUNTRY_CONFIG[key].code === countryCode
    ) || null;
}

/**
 * Validates if a country code is supported
 * @param {string} countryCode - The country code to validate
 * @returns {boolean} - True if valid
 */
export function validateCountry(countryCode) {
    if (!countryCode) return false;
    return Object.values(COUNTRY_CONFIG).some(config => config.code === countryCode);
}

/**
 * Checks if we're in development mode (localhost)
 * @returns {boolean} - True if in development
 */
export function isDevelopment() {
    if (typeof window === 'undefined') return false;
    const hostname = window.location.hostname;
    return hostname === 'localhost' || hostname === '127.0.0.1' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname);
}

/**
 * Gets the URL for a country code using subdomain-based routing
 * @param {string} countryCode - The country code (e.g., 'PE')
 * @returns {string} - The full URL (e.g., 'http://localhost:3001?country=PE' or 'https://peru.lotespro.com')
 */
export function getCountryUrl(countryCode) {
    const countryKey = getCountryKey(countryCode);

    if (isDevelopment()) {
        // Development: use query parameter
        if (typeof window !== 'undefined') {
            const { protocol, hostname, port } = window.location;
            const portPart = port ? `:${port}` : '';
            return `${protocol}//${hostname}${portPart}?country=${countryCode}`;
        }
        return `http://localhost:3001?country=${countryCode}`;
    }

    // Production: use subdomain
    if (!countryKey) {
        return 'https://lotespro.com';
    }

    return `https://${countryKey}.lotespro.com`;
}

/**
 * Gets the currency symbol for a given currency code
 * @param {string} currencyCode - The currency code (e.g., 'MXN', 'PEN', 'USD')
 * @returns {string} - The currency symbol (e.g., '$', 'S/', 'Bs.')
 */
export function getCurrencySymbol(currencyCode) {
    if (!currencyCode) return '$';

    // Build mapping from COUNTRY_CONFIG
    const symbolMap = {};
    Object.values(COUNTRY_CONFIG).forEach(config => {
        if (config.currency && config.currencySymbol) {
            symbolMap[config.currency] = config.currencySymbol;
        }
    });

    return symbolMap[currencyCode] || '$';
}

/**
 * Formats a price according to country locale and currency
 * @param {number} price - The price to format
 * @param {string} countryCode - The country code
 * @returns {string} - Formatted price string
 */
export function formatPrice(price, countryCode) {
    const config = getCountryConfig(countryCode);
    if (!config) return price.toString();

    try {
        return new Intl.NumberFormat(config.locale, {
            style: 'currency',
            currency: config.currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(price);
    } catch (error) {
        // Fallback to simple format
        return `${config.currencySymbol}${price.toLocaleString()}`;
    }
}

/**
 * Gets URL with proper path (subdomain architecture doesn't need country in path)
 * @param {string} path - The path to navigate to (e.g., '/inicio', '/proyectos/mi-proyecto')
 * @returns {string} - The clean path without country prefix
 */
export function getUrlWithCountry(path) {
    // Clean the path - remove leading slash
    const cleanPath = path.replace(/^\//, '');

    // In subdomain architecture, we don't need country in path
    // Just return the clean path
    return `/${cleanPath}`;
}

/**
 * Legacy function - no longer needed with subdomain architecture
 * @deprecated Paths no longer contain country slugs
 */
export function removeCountryFromPath(path) {
    return path || '/';
}
